import React, { useEffect, useState } from "react";
import moment from "moment";
import { DateRangePicker } from "react-date-range";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import MainLayoutWithoutFooter from "./MainLayoutWithoutFooter";
import { callAPI } from "../utils/apiUtils";
import { apiUrls } from "../utils/apiUrls";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "../styles/calander.css";

/**
 * Post-PIN view for the Timesheet page. `employee` comes from
 * TimesheetController::actionValidatePin ({employee_id, first_name, last_name, role,
 * is_checked_in, checked_in_at}) - the new Staff/Roles/Permissions module's PIN system, not the
 * old employee-list-then-PIN flow.
 */
const Timesheet = ({ employee, onSwitchUser }) => {
  const navigate = useNavigate();
  const [isCheckedIn, setIsCheckedIn] = useState(!!employee.is_checked_in);
  const [toggling, setToggling] = useState(false);
  const [clockTime, setClockTime] = useState(moment().format("hh:mm:ss A"));
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [activeQuickRange, setActiveQuickRange] = useState("today");
  const [range, setRange] = useState([
    {
      startDate: moment().startOf("day").toDate(),
      endDate: moment().endOf("day").toDate(),
      key: "selection",
    },
  ]);

  const start = moment(range[0].startDate);
  const end = moment(range[0].endDate);

  useEffect(() => {
    const timer = setInterval(() => setClockTime(moment().format("hh:mm:ss A")), 1000);
    return () => clearInterval(timer);
  }, []);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await callAPI(
        `${apiUrls.TIMESHEET_HISTORY}?employee_id=${employee.employee_id}&start_date=${start.format("YYYY-MM-DD")}&end_date=${end.format("YYYY-MM-DD")}`,
        {},
        "GET"
      );
      setHistory(res.data?.data || []);
    } catch (err) {
      toast.error("Failed to load history");
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const url = isCheckedIn ? apiUrls.TIMESHEET_CHECKOUT : apiUrls.TIMESHEET_CHECKIN;
      const res = await callAPI(url, {}, "POST", { employee_id: employee.employee_id });
      if (res.data?.data) {
        const nowCheckedIn = res.data.data.is_checked_in;
        setIsCheckedIn(nowCheckedIn);
        toast.success(nowCheckedIn ? "Checked in" : "Checked out");
        loadHistory();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Something went wrong");
    } finally {
      setToggling(false);
    }
  };

  const setQuickRange = (key) => {
    let s;
    let e;
    if (key === "today") {
      s = moment().startOf("day");
      e = moment().endOf("day");
    } else if (key === "yesterday") {
      s = moment().subtract(1, "day").startOf("day");
      e = moment().subtract(1, "day").endOf("day");
    } else if (key === "7") {
      s = moment().subtract(6, "days").startOf("day");
      e = moment().endOf("day");
    } else if (key === "30") {
      s = moment().subtract(29, "days").startOf("day");
      e = moment().endOf("day");
    }

    setRange([{ startDate: s.toDate(), endDate: e.toDate(), key: "selection" }]);
    setActiveQuickRange(key);
  };

  return (
    <MainLayoutWithoutFooter>
      <div className="pageContent timesheetsPage pb-3">
        <div className="container">

          <div className="sbRow mb-3 mb-lg-4">
            <div>
              <h2 className="lgTitle mb-1">Timesheet</h2>
              <p className="mb-0" style={{ color: "rgba(255,255,255,0.55)", fontSize: 14 }}>
                {employee.first_name} {employee.last_name}
                {employee.role ? ` · ${employee.role}` : ""}
              </p>
            </div>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              {employee.can_view_team && employee.team_token && (
                <button
                  type="button"
                  className="clock-btn"
                  style={{ background: "var(--primary)", color: "#fff", padding: "8px 16px", borderRadius: 8 }}
                  onClick={() => navigate("/team-timesheets", { state: { employee } })}
                >
                  View all staff timesheets
                </button>
              )}
              <button type="button" className="underlineLink" onClick={onSwitchUser}>
                Not you? Switch user
              </button>
            </div>
          </div>

          <div className="row gx-2 gy-3 gy-lg-0 align-items-center mb-4">

            <div className="col-md-auto">
              <div className="time-box">
                <div className="icon">⏱</div>
                <span id="current-time">{clockTime}</span>
                <button
                  className="clock-btn"
                  onClick={handleToggle}
                  disabled={toggling}
                  style={{ background: isCheckedIn ? "#d64545" : "#2f9e44", color: "#fff", minWidth: 92 }}
                >
                  {toggling ? "..." : isCheckedIn ? "Check Out" : "Check In"}
                </button>
              </div>
            </div>

            <div className="col-md-auto">
              <div
                className="custom-date-box"
                onClick={() => setShowPicker(!showPicker)}
                style={{ cursor: "pointer" }}
              >
                <span>
                  {start.format("MMM DD, YYYY")} - {end.format("MMM DD, YYYY")}
                </span>
              </div>

              {showPicker && (
                <div style={{ position: "absolute", zIndex: 10 }}>
                  <DateRangePicker
                    onChange={(item) => {
                      setRange([item.selection]);
                      setShowPicker(false);
                    }}
                    ranges={range}
                    moveRangeOnFirstSelection={false}
                  />
                </div>
              )}
            </div>

            <div className="col-md-auto">
              <div className="btnGroup tableFilterBtns">
                <button
                  className={`radioBtn ${activeQuickRange === "today" ? "active" : ""}`}
                  onClick={() => setQuickRange("today")}
                >
                  Today
                </button>
                <button
                  className={`radioBtn ${activeQuickRange === "yesterday" ? "active" : ""}`}
                  onClick={() => setQuickRange("yesterday")}
                >
                  Yesterday
                </button>
                <button
                  className={`radioBtn ${activeQuickRange === "7" ? "active" : ""}`}
                  onClick={() => setQuickRange("7")}
                >
                  Last 7 days
                </button>
                <button
                  className={`radioBtn ${activeQuickRange === "30" ? "active" : ""}`}
                  onClick={() => setQuickRange("30")}
                >
                  Last 30 days
                </button>
              </div>
            </div>
          </div>

          <div className="cardStyle">
            <table className="table tableStyle2 table-borderless">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Date</th>
                  <th>Clock-In</th>
                  <th>Clock-Out</th>
                  <th>Shift Time</th>
                </tr>
              </thead>
              <tbody>
                {loadingHistory ? (
                  <tr>
                    <td colSpan="5" className="text-center">Loading...</td>
                  </tr>
                ) : history.length > 0 ? (
                  history.map((row, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{row.created_at ? moment(row.created_at).format("MMM DD, YYYY") : "-"}</td>
                      <td>{row.check_in ? moment(row.check_in).format("hh:mm A") : "-"}</td>
                      <td>{row.check_out ? moment(row.check_out).format("hh:mm A") : "-"}</td>
                      <td>{row.time_diff ?? "-"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">No records found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </MainLayoutWithoutFooter>
  );
};

export default Timesheet;
